import * as React from "react"
import { Box, Typography, Chip, Avatar, Tooltip, IconButton, Button } from "@mui/material"
import { IconSeeker, FlagsIconSeeker } from "@ui-seeker/core"
import { postSubmit } from "@ui-seeker/utils"

import { editMappingDrawer, imageComparisonModal, tableData } from "../@signals"
import { BACKEND_APIS } from "@constants"


function formatRelativeDate(dateString) {
    if (!dateString) return '-'
    
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = now - date
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays < 0) return 'Future'
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 14) return '1 week ago'
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    if (diffDays < 60) return '1 month ago'
    return `${Math.floor(diffDays / 30)} months ago`
}

function getCurrencySymbol(currencyCode) {
    const currencyMap = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'JPY': '¥',
        'CNY': '¥',
        'MXN': '$',
        'MEX': '$',
        'BRL': 'R$',
        'ARS': '$',
        'CLP': '$',
        'COP': '$',
        'PEN': 'S/',
        'CAD': 'C$',
        'AUD': 'A$',
        'CHF': 'CHF',
        'INR': '₹',
        'RUB': '₽',
        'KRW': '₩',
        'TRY': '₺',
        'ZAR': 'R',
        'SEK': 'kr',
        'NOK': 'kr',
        'DKK': 'kr',
        'PLN': 'zł',
        'THB': '฿',
        'IDR': 'Rp',
        'MYR': 'RM',
        'PHP': '₱',
        'SGD': 'S$',
        'NZD': 'NZ$',
        'HKD': 'HK$'
    }
    return currencyMap[currencyCode] || currencyCode || '$'
}


export const searchTextColumn = () => ({
    name: 'search_text',
    label: 'Search',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true,
        viewColumns: false
    }
})

export const productIdColumn = () => ({
    name: 'product_id',
    label: 'Product ID',
    options: {
        filter: false,
        sort: true,
        display: false,
        searchable: true
    }
})

export const skuIdColumn = () => ({
    name: 'id_sku',
    label: 'SKU ID',
    options: {
        filter: false,
        sort: true,
        display: false,
        searchable: true
    }
})

export const eanColumn = () => ({
    name: 'ean',
    label: 'EAN',
    options: {
        filter: false,
        sort: true,
        display: false,
        searchable: true
    }
})

export const rawTitleColumn = () => ({
    name: 'raw_title',
    label: 'Raw Title',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const rawSubtitleColumn = () => ({
    name: 'raw_subtitle',
    label: 'Raw Subtitle',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const rawSupertitleColumn = () => ({
    name: 'raw_supertitle',
    label: 'Raw Supertitle',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const rawVariantColumn = () => ({
    name: 'raw_variant',
    label: 'Raw Variant',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const skuNameColumn = () => ({
    name: 'sku_name',
    label: 'SKU Name',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const mappingStatusColumn = () => ({
    name: 'mapping_status',
    label: 'Mapping Status',
    options: {
        filter: true,
        filterType: 'checkbox',
        sort: true,
        display: false
    }
})

export const lastSeenFilterColumn = () => ({
    name: 'last_seen_filter',
    label: 'Last Seen',
    options: {
        filter: true,
        filterType: 'checkbox',
        sort: false,
        display: false,
        filterOptions: {
            names: ['Today', 'Yesterday', 'Last 7 days', 'Last 30 days', 'Older'],
            logic: (value, filters) => {
                if (!filters.length) return false
                return !filters.includes(value)
            }
        }
    }
})

export const newProductFilterColumn = () => ({
    name: 'new_product_status',
    label: 'New Products',
    options: {
        filter: true,
        filterType: 'checkbox',
        sort: false,
        display: false,
        filterOptions: {
            names: ['New (24h)', 'New (7 days)', 'Not new'],
            logic: (value, filters) => {
                if (!filters.length) return false
                return !filters.includes(value)
            }
        }
    }
})

export const siteCountryCodeColumn = () => ({
    name: 'siteCountryCode',
    label: 'Country Code',
    options: {
        filter: false,
        sort: false,
        display: false,
        viewColumns: false
    }
})

export const siteColumn = () => ({
    name: 'site',
    label: 'Site',
    options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value, tableMeta) => {
            const countryCode = tableMeta.rowData[12]
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    {countryCode && <FlagsIconSeeker countryName={countryCode} />}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {value || '-'}
                    </Typography>
                </Box>
            )
        }
    }
})

export const brandColumn = () => ({
    name: 'brand',
    label: 'Brand',
    options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value, tableMeta) => {
            const raw = tableMeta.rowData[17]
            const isNormalized = !!raw?.id_brand

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#6366f1' }}>
                        {value || '-'}
                    </Typography>
                    {value && value !== '-' && !isNormalized && (
                        <Tooltip title="Brand not normalized — candidates won't load on edit" arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: '#f59e0b', cursor: 'help' }}>
                                <IconSeeker icon="warning" sx={{ fontSize: 16 }} />
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            )
        }
    }
})

export const brandStatusFilterColumn = () => ({
    name: 'brand_status',
    label: 'Brand Status',
    options: {
        filter: true,
        filterType: 'checkbox',
        sort: false,
        display: false
    }
})

export const activityColumn = () => ({
    name: 'activity',
    label: 'Activity',
    options: {
        filter: false,
        sort: true,
        sortCompare: (order) => (a, b) => {
            const dateA = new Date(a.data?.end_date || 0)
            const dateB = new Date(b.data?.end_date || 0)
            return order === 'asc' ? dateA - dateB : dateB - dateA
        },
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Tooltip title={value?.start_date || '-'}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: '#6366f1',
                            fontWeight: 500,
                            cursor: 'help',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        <span style={{ color: '#9ca3af', fontWeight: 400 }}>First:</span>
                        {formatRelativeDate(value?.start_date)}
                    </Typography>
                </Tooltip>
                <Tooltip title={value?.end_date || '-'}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            color: '#059669',
                            fontWeight: 500,
                            cursor: 'help',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        <span style={{ color: '#9ca3af', fontWeight: 400 }}>Last:</span>
                        {formatRelativeDate(value?.end_date)}
                    </Typography>
                </Tooltip>
            </Box>
        )
    }
})

export const rawProductColumn = () => ({
    name: 'raw',
    label: 'Raw Product',
    options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ 
            align: 'left',
            sx: { backgroundColor: '#fef3c7' }
        }),
        setCellProps: () => ({ align: 'left' }),
        customBodyRender: (raw, tableMeta) => {
            if (!raw) return '-'
            
            // Get SKU from row data (index 18 after all hidden columns including siteCountryCode and brand_status)
            const sku = tableMeta.rowData[18]
            
            const handleImageClick = (e) => {
                e.stopPropagation()
                imageComparisonModal.value = {
                    title: raw?.title,
                    rawImage: raw?.image,
                    skuImage: sku?.id_sku ? `${BACKEND_APIS.BACKEND}/sku/image/${sku.id_sku}` : null
                }
            }
            
            return (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Tooltip title="Click to compare images">
                        <Avatar
                            src={raw?.image}
                            variant="rounded"
                            onClick={handleImageClick}
                            sx={{ 
                                width: 70, 
                                height: 70, 
                                bgcolor: '#f3f4f6',
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '& img': {
                                    objectFit: 'contain'
                                },
                                '&:hover': {
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }
                            }}
                        >
                            <IconSeeker icon="image" sx={{ color: '#9ca3af' }} />
                        </Avatar>
                    </Tooltip>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                            <Tooltip title={raw?.title || '-'} placement="top">
                                {raw?.product_url ? (
                                    <a
                                        href={raw.product_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 4 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                wordBreak: 'break-word',
                                                lineHeight: 1.3,
                                                color: 'text.primary',
                                                '&:hover': {
                                                    textDecoration: 'underline'
                                                }
                                            }}
                                        >
                                            {raw?.title || '-'}
                                        </Typography>
                                        <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 14, color: '#6366f1', flexShrink: 0, mt: 0.25 }} />
                                    </a>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            wordBreak: 'break-word',
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {raw?.title || '-'}
                                    </Typography>
                                )}
                            </Tooltip>
                        </Box>
                        {raw?.subtitle && (
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {raw.subtitle}
                            </Typography>
                        )}
                        {raw?._productCount > 1 ? (
                            <Tooltip
                                title={raw._groupedSizes?.length > 0 ? `Sizes: ${raw._groupedSizes.join(', ')}` : ''}
                                placement="top"
                            >
                                <Chip
                                    icon={<IconSeeker icon="layer-group" sx={{ fontSize: 12 }} />}
                                    label={`${raw._productCount} products`}
                                    size="small"
                                    sx={{
                                        mt: 0.5,
                                        fontSize: '0.7rem',
                                        height: 22,
                                        fontWeight: 700,
                                        backgroundColor: '#e0f2fe',
                                        color: '#0369a1',
                                        '& .MuiChip-icon': { color: '#0369a1' }
                                    }}
                                />
                            </Tooltip>
                        ) : raw?.variant_raw && (
                            <Tooltip title={raw.variant_raw} placement="top">
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                        display: 'inline',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        backgroundColor: '#e0e7ff',
                                        color: '#4338ca',
                                        mt: 0.5,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        lineHeight: 1.8,
                                        boxDecorationBreak: 'clone',
                                        WebkitBoxDecorationBreak: 'clone'
                                    }}
                                >
                                    {raw.variant_raw}
                                </Typography>
                            </Tooltip>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                ID: {raw?.product_id || '-'}
                            </Typography>
                            {raw?.ean && (
                                <Tooltip title="EAN">
                                    <Chip
                                        icon={<IconSeeker icon="barcode" sx={{ fontSize: 12 }} />}
                                        label={raw.ean}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </Tooltip>
                            )}
                            {raw?.reference && (
                                <Tooltip title="Reference">
                                    <Chip
                                        icon={<IconSeeker icon="tag" sx={{ fontSize: 12 }} />}
                                        label={raw.reference}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                        {raw?.actual_price > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#059669',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    {getCurrencySymbol(raw.currency)}{raw.actual_price.toFixed(2)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            )
        }
    }
})

export const mappedSkuColumn = () => ({
    name: 'sku',
    label: 'Mapped SKU',
    options: {
        filter: false,
        sort: true,
        sortCompare: (order) => (a, b) => {
            const aHasSku = a.data && a.data.id_sku
            const bHasSku = b.data && b.data.id_sku
            
            // If both have SKU or both don't have SKU, maintain original order
            if (aHasSku === bHasSku) return 0
            
            // Sort unmapped first when ascending, mapped first when descending
            if (order === 'asc') {
                return aHasSku ? 1 : -1  // Unmapped first
            } else {
                return aHasSku ? -1 : 1  // Mapped first
            }
        },
        setCellHeaderProps: () => ({ 
            align: 'left',
            sx: { backgroundColor: '#d1fae5' }
        }),
        setCellProps: () => ({ align: 'left' }),
        customBodyRender: (sku, tableMeta) => {
            const raw = tableMeta.rowData[17]
            const groupStatus = raw?._groupStatus
            const productCount = raw?._productCount || 1

            // Group-specific warnings
            if (groupStatus === 'partial') {
                const mappedCount = raw._groupedProducts.filter(i => i.sku).length
                const unmappedCount = raw._unmappedProducts?.length || 0

                const handleMapMissing = async (e) => {
                    e.stopPropagation()
                    const skuId = raw._mappedSkuId
                    if (!skuId) return
                    try {
                        const promises = raw._unmappedProducts.map(item =>
                            postSubmit(`/api/mapping/sku`, {
                                productID: item.raw?.product_id,
                                siteID: item.raw?.id_site,
                                skuID: skuId,
                                source: 'product-review-group-map-missing'
                            })
                        )
                        await Promise.all(promises)
                        // Find the mapped SKU data from a mapped sibling
                        const mappedSibling = raw._groupedProducts.find(i => i.sku)
                        const skuData = mappedSibling?.sku
                        const unmappedKeys = raw._unmappedProducts.map(i => `${i.raw?.product_id}-${i.raw?.id_site}`)
                        tableData.value = tableData.value.map(item => {
                            const key = `${item.raw?.product_id}-${item.raw?.id_site}`
                            if (unmappedKeys.includes(key)) {
                                return { ...item, sku: skuData ? { ...skuData } : { id_sku: skuId } }
                            }
                            return item
                        })
                    } catch (error) {
                        console.error('Error mapping missing products:', error)
                    }
                }

                return (
                    <Box>
                        {sku && (
                            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                <Avatar
                                    src={sku?.image}
                                    variant="rounded"
                                    sx={{ width: 50, height: 50, bgcolor: '#ecfdf5', border: '2px solid #10b981', '& img': { objectFit: 'contain' } }}
                                >
                                    <IconSeeker icon="check" sx={{ color: '#10b981' }} />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                                        {sku?.name || '-'}
                                    </Typography>
                                    <Chip label={`SKU: ${sku?.id_sku || '-'}`} size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />
                                </Box>
                            </Box>
                        )}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: '#fef3c7',
                            border: '1px solid #fbbf24'
                        }}>
                            <IconSeeker icon="triangle-exclamation" sx={{ fontSize: 16, color: '#d97706' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', flex: 1 }}>
                                {unmappedCount} of {productCount} products unmapped
                            </Typography>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleMapMissing}
                                sx={{
                                    fontSize: '0.65rem',
                                    py: 0.25,
                                    px: 1,
                                    minWidth: 'auto',
                                    bgcolor: '#d97706',
                                    '&:hover': { bgcolor: '#b45309' }
                                }}
                            >
                                Map missing ({unmappedCount})
                            </Button>
                        </Box>
                    </Box>
                )
            }

            if (groupStatus === 'inconsistent') {
                return (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: '#fef2f2',
                        border: '2px solid #ef4444'
                    }}>
                        <IconSeeker icon="circle-exclamation" sx={{ fontSize: 20, color: '#dc2626' }} />
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                                Inconsistent mappings!
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#991b1b' }}>
                                Products mapped to different SKUs — review required
                            </Typography>
                        </Box>
                    </Box>
                )
            }

            if (!sku) {
                return (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 2,
                        color: '#9ca3af'
                    }}>
                        <IconSeeker icon="link-slash" sx={{ mr: 1 }} />
                        <Typography variant="body2">Not mapped</Typography>
                    </Box>
                )
            }

            const isNotValidated = sku?.validated === 0 || sku?.validated === false

            return (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ position: 'relative' }}>
                        <Avatar
                            src={sku?.image}
                            variant="rounded"
                            sx={{
                                width: 70,
                                height: 70,
                                bgcolor: isNotValidated ? '#fef3c7' : '#ecfdf5',
                                border: isNotValidated ? '2px solid #f59e0b' : '2px solid #10b981',
                                '& img': {
                                    objectFit: 'contain'
                                }
                            }}
                        >
                            <IconSeeker icon="check" sx={{ color: isNotValidated ? '#f59e0b' : '#10b981' }} />
                        </Avatar>
                        {isNotValidated && (
                            <Tooltip title="Mapping not validated">
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -8,
                                        right: -8,
                                        bgcolor: '#f59e0b',
                                        borderRadius: '50%',
                                        width: 24,
                                        height: 24,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    <IconSeeker icon="triangle-exclamation" sx={{ fontSize: 14, color: 'white' }} />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Tooltip title={sku?.name || '-'} placement="top">
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: isNotValidated ? '#d97706' : '#059669',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.3
                                    }}
                                >
                                    {sku?.name || '-'}
                                </Typography>
                            </Tooltip>
                            {isNotValidated && (
                                <Chip
                                    label="Not validated"
                                    size="small"
                                    sx={{
                                        fontSize: '0.65rem',
                                        height: 18,
                                        bgcolor: '#fef3c7',
                                        color: '#92400e',
                                        fontWeight: 600
                                    }}
                                />
                            )}
                        </Box>
                        {sku?.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {sku.description}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                                label={`SKU: ${sku?.id_sku || '-'}`}
                                size="small"
                                color="success"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                            {sku?.brand && (
                                <Chip
                                    label={sku.brand}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            )
        }
    }
})

export const actionsColumn = () => ({
    name: 'actions',
    label: 'Actions',
    options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value, tableMeta) => {
            const rowData = tableMeta.rowData
            // raw is at index 17, sku is at index 18 (after all hidden columns including siteCountryCode and brand_status)
            const raw = rowData[17]
            const sku = rowData[18]
            const isNotValidated = sku && (sku.validated === 0 || sku.validated === false)

            const groupedProducts = raw?._groupedProducts
            const isGroupedRow = groupedProducts && groupedProducts.length > 1

            const handleEdit = (e) => {
                e.stopPropagation()
                editMappingDrawer.value = isGroupedRow
                    ? { raw, sku, groupedProducts }
                    : { raw, sku }
            }

            const handleValidate = async (e) => {
                e.stopPropagation()
                try {
                    const products = isGroupedRow ? groupedProducts : [{ raw, sku }]
                    const mappings = products
                        .filter(p => {
                            const s = p.sku || (p === products[0] ? sku : null)
                            return s && (s.validated === 0 || s.validated === false)
                        })
                        .map(p => ({
                            id_site: (p.raw || raw)?.id_site,
                            product_id: (p.raw || raw)?.product_id
                        }))

                    if (mappings.length === 0) return

                    await postSubmit(`/api/products-review/validate`, { mappings })

                    const validatedKeys = mappings.map(m => `${m.product_id}-${m.id_site}`)
                    tableData.value = tableData.value.map(item => {
                        const key = `${item.raw?.product_id}-${item.raw?.id_site}`
                        if (validatedKeys.includes(key)) {
                            return { ...item, sku: { ...item.sku, validated: 1 } }
                        }
                        return item
                    })
                } catch (error) {
                    console.error('Error validating mapping:', error)
                }
            }

            const handleRemove = async (e) => {
                e.stopPropagation()
                try {
                    const products = isGroupedRow ? groupedProducts : [{ raw }]
                    const promises = products
                        .filter(p => (isGroupedRow ? p.sku : sku))
                        .map(p =>
                            postSubmit(`/api/mapping/sku`, {
                                productID: (p.raw || raw)?.product_id,
                                siteID: (p.raw || raw)?.id_site,
                                skuID: 0,
                                source: 'product-review'
                            })
                        )

                    await Promise.all(promises)

                    const removedKeys = products.map(p => `${(p.raw || raw)?.product_id}-${(p.raw || raw)?.id_site}`)
                    tableData.value = tableData.value.map(item => {
                        const key = `${item.raw?.product_id}-${item.raw?.id_site}`
                        if (removedKeys.includes(key)) {
                            return { ...item, sku: null }
                        }
                        return item
                    })
                } catch (error) {
                    console.error('Error removing mapping:', error)
                }
            }

            const hasUnvalidated = isGroupedRow
                ? groupedProducts.some(p => p.sku && (p.sku.validated === 0 || p.sku.validated === false))
                : isNotValidated
            const hasMapped = isGroupedRow
                ? groupedProducts.some(p => p.sku)
                : !!sku

            return (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={isGroupedRow ? `Edit mapping (${groupedProducts.length} products)` : "Edit mapping"}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={handleEdit}
                        >
                            <IconSeeker icon="pen-to-square" />
                        </IconButton>
                    </Tooltip>
                    {hasUnvalidated && (
                        <Tooltip title={isGroupedRow ? `Validate all mappings` : "Validate mapping"}>
                            <IconButton
                                size="small"
                                sx={{ color: '#f59e0b' }}
                                onClick={handleValidate}
                            >
                                <IconSeeker icon="check-circle" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {hasMapped && (
                        <Tooltip title={isGroupedRow ? `Remove all mappings` : "Remove mapping"}>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={handleRemove}
                            >
                                <IconSeeker icon="link-slash" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )
        }
    }
})
